import { useProjectParams } from '@hooks/useProjectParams';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { Toolbar } from 'components/Toolbar';
import { TooltipWrapper } from 'components/TooltipWrapper';
import {
    findAllConstructionTypesFromObjects,
    WorkOrderConstructionTypesFromObjects,
} from 'components/WorkOrderConstructionTypesTabSelect';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimChapterEnum, BimPriceLevelEnum, ConstructionTypeData } from 'gql/graphql';
import { ContractStatusEnum, ProjectContractTypeEnum, SectionEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { usePreviousValue } from 'hooks/usePrevious';
import { useProjectData } from 'hooks/useProjectData';
import { BimPriceLevelChoice } from 'pages/Project/BimProject/BimCommonComponents/BimPriceLevelChoice';
import { BimWorkOrderFilterWithTools } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderFilter';
import { BimWorkOrderPriceCounter } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceCounter';
import {
    isBimChapter,
    isOtherBimChapter,
    isTotalsBimChapter,
    totalsByConstructionTypeKey,
} from 'pages/Project/BimProject/WorkOrder/types';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import type { IListItem } from 'types/Contract';
import { Layout } from 'uikit';
import { formatDateToLocale, isNumber } from 'utils';
import { workOrderChangeDescriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getLabelWorkOrderNoSingingDate } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import type { CSSProperties, ElementRef } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { BimOrderChangeRequisites } from '../BimOrderChangeRequisites';
import { BimWorkOrderChangeKebabMenu } from '../BimWorkOrderChangeKebabMenu';
import { BimWorkOrderChangeOthersTable } from '../BimWorkOrderChangeOthersTable';
import { BimWorkOrderChangePriceTable } from '../BimWorkOrderChangePriceTable';
import { BimWorkOrderChangeTotalsTable } from '../BimWorkOrderChangeTotalsTable';
import { BimWorkOrderChangeUploads } from '../BimWorkOrderChangeUploads';

import {
    useBimWorkOrderChange,
    useBimWorkOrderChangePriceCounter,
    useCorrectUpdateWorkOrderChangeData,
    useGetWorkOrderChangeObjects,
} from './hooks';

import './bimWorkOrderChangeTable.css';

const Style: CSSProperties = { marginBottom: 0 };

export const BimWorkOrderChange = () => {
    const { id: projectId, orderChangeId, methodology } = useProjectParams();

    const prevOrderChangeId = usePreviousValue(orderChangeId);

    const { projectData, refreshProject, projectDataStatus } = useProjectData(projectId);
    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const [workOrderChangeRootObject, setWorkOrderChangeRootObject] = useState<IListItem<number> | undefined>();
    const [workOrderChangeSectionOrChapter, setWorkOrderChangeSectionOrChapter] = useState<
        IListItem<string> | undefined
    >();
    const [constructionType, setConstructionType] = useState<ConstructionTypeData | undefined>();
    const [position, setPosition] = useState<number | undefined>();
    const [priceLevel, setPriceLevel] = useState<BimPriceLevelEnum>();

    const workOrderChangeFilterRef = useRef<ElementRef<typeof BimWorkOrderFilterWithTools>>(null);
    const resetCheckedRowsRef = useRef<() => void>();
    const priceTableApiRef = useRef<AgGridReact>(null);

    const { orderBim: orderRules } = useAccess();

    const currentWorkOrder = useMemo(
        () =>
            projectData?.contractNavigation?.data?.workOrderNavs?.find(
                (workOrder) =>
                    workOrder.workOrderChanges?.some((workOrderChange) => workOrderChange.id === workOrderChangeId),
            ),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderChangeId],
    );

    const { workOrderChangeData, bimWorkOrderChangeStatus, refreshWorkOrderChangeData } =
        useBimWorkOrderChange(workOrderChangeId);

    const { workOrderChangeObjectsData, refreshWorkOrderChangeObjects } = useGetWorkOrderChangeObjects({
        workOrderChangeId,
    });

    const statusProject = projectData?.status;

    useEffect(() => {
        if (workOrderChangeFilterRef.current) {
            setPosition(workOrderChangeFilterRef.current?.getBoundingClientRect().bottom);
        }
    }, [
        workOrderChangeFilterRef,
        isHeaderOpened,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        workOrderChangeFilterRef.current?.getBoundingClientRect().bottom,
    ]);

    const projectStatus = projectData?.status;
    const contractStatus = projectData?.contractNavigation?.data?.contractNavData?.status;

    const titleWorkOrderChange = useMemo(
        () =>
            `Изм.${workOrderChangeDescriptionNumberAndDate(
                workOrderChangeData?.internalNumber,
                workOrderChangeData?.signingDate,
                currentWorkOrder?.internalNumber,
            )}`,
        [currentWorkOrder?.internalNumber, workOrderChangeData?.internalNumber, workOrderChangeData?.signingDate],
    );

    const workOrderChangeShortTitle = useMemo(
        () =>
            `Изм.№ ${workOrderChangeData?.internalNumber} ${
                workOrderChangeData?.signingDate ? `от ${formatDateToLocale(workOrderChangeData?.signingDate)}  ` : ''
            }`,
        [workOrderChangeData],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
            {
                label: getLabelWorkOrderNoSingingDate(currentWorkOrder),
                href: `/projects/${methodology}/${projectData?.uniqueId}/order/${currentWorkOrder?.id}`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId, currentWorkOrder],
    );

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    const bimChapter = useMemo(
        () =>
            isBimChapter(workOrderChangeSectionOrChapter?.id)
                ? (workOrderChangeSectionOrChapter?.id as BimChapterEnum)
                : undefined,
        [workOrderChangeSectionOrChapter?.id],
    );

    const bimOtherChapter = useMemo(
        () =>
            isOtherBimChapter(workOrderChangeSectionOrChapter?.id)
                ? (workOrderChangeSectionOrChapter?.id as BimChapterEnum)
                : undefined,
        [workOrderChangeSectionOrChapter?.id],
    );

    const bimTotalsChapter = useMemo(
        () =>
            isTotalsBimChapter(workOrderChangeSectionOrChapter?.id) ? workOrderChangeSectionOrChapter?.id : undefined,
        [workOrderChangeSectionOrChapter?.id],
    );

    const onRequisitesEditSuccess = useCallback(() => {
        resetCheckedRowsRef?.current?.();
    }, []);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: ({ savedValuesObj }) => {
                    const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
                    const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

                    const savedConstructionType = findAllConstructionTypesFromObjects({
                        workOrderChangeObjectsData,
                    }).find((type) => type.id === savedConstructionTypeId);

                    if (savedConstructionType) {
                        setConstructionType(savedConstructionType);
                    }

                    setWorkOrderChangeSectionOrChapter(savedFilters?.chapter ?? undefined);
                    setWorkOrderChangeRootObject(savedFilters?.object ?? undefined);
                },
                withEmptyFilterSetter:
                    workOrderChangeObjectsData?.length && prevOrderChangeId === orderChangeId
                        ? (setter: (params: { key: string | undefined; newState: any }) => void) => {
                              const constructionTypes = findAllConstructionTypesFromObjects({
                                  workOrderChangeObjectsData,
                              });
                              const [firstConstructionType] = constructionTypes;

                              setConstructionType(firstConstructionType);
                              setter({
                                  key: 'constructionTypeId',
                                  newState: firstConstructionType?.id,
                              });
                          }
                        : undefined,
            }),
            [orderChangeId, prevOrderChangeId, workOrderChangeObjectsData],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionType: ConstructionTypeData) => {
            setConstructionType(newConstructionType);

            setTimeout(() => {
                setWorkOrderChangeSectionOrChapter(savedFilterState?.[newConstructionType?.id]?.object ?? undefined);
                setWorkOrderChangeRootObject(savedFilterState?.[newConstructionType?.id]?.chapter ?? undefined);
            }, 0);

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionType.id,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const onRootObjectChange = useCallback(
        (rootObject: IListItem<number> | undefined) => {
            setWorkOrderChangeRootObject(rootObject);

            handleSaveFilterState({
                rootKey: String(constructionType?.id),
                key: 'object',
                newState: rootObject,
            });
        },
        [constructionType?.id, handleSaveFilterState],
    );

    const onSectionOrChapterChange = useCallback(
        (sectionOrChapter: IListItem<string>) => {
            setWorkOrderChangeSectionOrChapter(sectionOrChapter);

            const isTotalsChapter =
                sectionOrChapter?.id === totalsByConstructionTypeKey ||
                sectionOrChapter?.id === SectionEnum.WorkOrderTotals;

            if (isTotalsChapter || !sectionOrChapter) {
                setWorkOrderChangeRootObject(undefined);

                handleSaveFilterState({
                    key: String(constructionType?.id),
                    newState: {
                        object: undefined,
                        chapter: sectionOrChapter,
                    },
                });
            } else {
                handleSaveFilterState({
                    rootKey: String(constructionType?.id),
                    key: 'chapter',
                    newState: sectionOrChapter,
                });
            }
        },
        [constructionType?.id, handleSaveFilterState],
    );

    const { priceListCounter, chaptersTooltip, refreshWorkOrderChangeChapterData } = useBimWorkOrderChangePriceCounter({
        workOrderSectionOrChapter: workOrderChangeSectionOrChapter,
        workOrderChangeId,
        workOrderRootObjectId: workOrderChangeRootObject?.workOrderObjectId,
        workOrderChangeRootObjectId: workOrderChangeRootObject?.workOrderChangeObjectId,
        constructionTypeId: constructionType?.id,
    });

    const isPageDataLoading =
        projectDataStatus === RequestStatus.FETCHING || bimWorkOrderChangeStatus === RequestStatus.FETCHING;

    const isShowPriceTablePossible = !!(
        (workOrderChangeRootObject && (bimChapter || bimOtherChapter)) ||
        bimTotalsChapter
    );

    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

    const isStatusWorkOrderChangeOnFormation = workOrderChangeData?.status === WorkOrderChangeStatusEnum.OnFormation;

    const isSectionAndObject = !workOrderChangeSectionOrChapter || !workOrderChangeRootObject || !workOrderChangeId;

    const isDisabledButtonAdd =
        isSectionAndObject ||
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        !isStatusWorkOrderChangeOnFormation;

    const isHiddenTooltipButtonAdd = !isSectionAndObject || isContractStatusDelayed || isContractStatusCompleted;

    const { makeRefreshCurrentTable } = useCorrectUpdateWorkOrderChangeData({
        workOrderChangeRootObject,
        workOrderChangeObjectsData,
        refreshWorkOrderChangeChapterData,
        refreshWorkOrderChangeObjects,
        currentTableApi: priceTableApiRef?.current,
        workOrderChangeRootObjectSetter: onRootObjectChange,
        workOrderChangeId,
        bimChapter,
        otherChapter: bimOtherChapter,
        constructionTypeId: constructionType?.id,
        priceLevel,
    });

    return (
        <ProjectPageLayout
            layoutEntityKey={orderChangeId}
            title={titleWorkOrderChange}
            statusWorkOrderChange={workOrderChangeData?.status}
            breadCrumbsItems={breadCrumbsItems}
            isHeaderCollapsable
            headerBottom={
                <Toolbar flex>
                    <BimOrderChangeRequisites
                        contractStatus={contractStatus}
                        isFrameContract={isFrameContract}
                        isLoading={isPageDataLoading}
                        onSuccess={onRequisitesEditSuccess}
                        projectData={projectData}
                        refreshProject={refreshProject}
                        refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                        workOrderChangeData={workOrderChangeData}
                        statusProject={statusProject}
                        workOrderStatus={currentWorkOrder?.status}
                        orderRules={orderRules}
                        statusWorkOrderChange={workOrderChangeData?.status}
                    />
                    <BimWorkOrderChangeKebabMenu
                        contractStatus={contractStatus}
                        projectStatus={projectStatus}
                        workOrderChangeStatus={workOrderChangeData?.status}
                        workOrderChangeId={workOrderChangeId}
                        workOrderId={currentWorkOrder?.id}
                        label={workOrderChangeShortTitle}
                    />
                    {!bimOtherChapter && <BimPriceLevelChoice priceLevel={priceLevel} setPriceLevel={setPriceLevel} />}
                </Toolbar>
            }
            style={Style}
        >
            <WorkOrderConstructionTypesFromObjects
                onChange={handleConstructionTypeChange}
                workOrderChangeObjectsData={workOrderChangeObjectsData}
                activeConstructionTypeId={constructionType?.id}
            />
            <BimWorkOrderFilterWithTools
                ref={workOrderChangeFilterRef}
                onRootObjectChange={onRootObjectChange}
                onSectionOrChapterChange={onSectionOrChapterChange}
                sectionOrChapter={workOrderChangeSectionOrChapter}
                rootObject={workOrderChangeRootObject}
                objectsListItems={workOrderChangeObjectsData}
                constructionTypeId={constructionType?.id}
                isFrameContract={isFrameContract}
                isWorkOrderChange
            >
                <Layout>
                    <BimWorkOrderPriceCounter priceListCounter={priceListCounter} chaptersTooltip={chaptersTooltip} />
                    <TooltipWrapper
                        isHidden={isHiddenTooltipButtonAdd}
                        direction="upCenter"
                        message="Выберите раздел и объект"
                    >
                        <BimWorkOrderChangeUploads
                            isDisabled={isDisabledButtonAdd}
                            workOrderChangeStatus={workOrderChangeData?.status}
                            workOrderStatus={currentWorkOrder?.status}
                            workOrderChangeId={workOrderChangeId}
                            chapter={bimChapter ?? bimOtherChapter}
                            selectedObject={workOrderChangeRootObject}
                            constructionTypeId={constructionType?.id}
                            makeRefreshCurrentTable={makeRefreshCurrentTable}
                            objectsListItems={workOrderChangeObjectsData}
                        />
                    </TooltipWrapper>
                </Layout>
            </BimWorkOrderFilterWithTools>
            {isShowPriceTablePossible ? (
                <div
                    id="bimWorkOrderChangeContainer"
                    style={
                        {
                            '--tableStartPosition': isNumber(position) && `${position ?? 0}px`,
                        } as CSSProperties
                    }
                >
                    {bimChapter && (
                        <BimWorkOrderChangePriceTable
                            ref={priceTableApiRef}
                            chapter={bimChapter}
                            workOrderChangeId={workOrderChangeId}
                            workOrderRootObjectId={workOrderChangeRootObject?.workOrderObjectId}
                            workOrderChangeRootObjectId={workOrderChangeRootObject?.workOrderChangeObjectId}
                            constructionTypeId={constructionType?.id}
                            workOrderChangeStatus={workOrderChangeData?.status}
                            makeRefreshTable={makeRefreshCurrentTable}
                            priceLevel={priceLevel}
                        />
                    )}
                    {bimTotalsChapter && (
                        <BimWorkOrderChangeTotalsTable
                            ref={priceTableApiRef}
                            chapter={bimTotalsChapter}
                            workOrderChangeId={workOrderChangeId}
                            constructionTypeId={constructionType?.id}
                            priceLevel={priceLevel}
                        />
                    )}
                    {bimOtherChapter && (
                        <BimWorkOrderChangeOthersTable
                            ref={priceTableApiRef}
                            workOrderChangeId={workOrderChangeId}
                            workOrderRootObjectId={workOrderChangeRootObject?.workOrderObjectId}
                            workOrderChangeRootObjectId={workOrderChangeRootObject?.workOrderChangeObjectId}
                            constructionTypeId={constructionType?.id}
                            workOrderChangeStatus={workOrderChangeData?.status}
                            makeRefreshTable={makeRefreshCurrentTable}
                        />
                    )}
                </div>
            ) : (
                <ChooseSectionText className="choiceMessage" text="Для отображения данных выберите раздел и объект" />
            )}
        </ProjectPageLayout>
    );
};

BimWorkOrderChange.displayName = 'BimWorkOrderChange';
